import { timestampToBusinessDate, toPersianDigits } from '@saay/formatting';
import { z } from 'zod';

/**
 * Admin-only input helpers. The shared input rules (national-code checksum, mobile normalization,
 * Solar Hijri parsing) live in `@saay/formatting`.
 */

/** Today's business date in Asia/Tehran (for hints and defaults; the server decides). */
export function tehranToday(now: Date = new Date()): string {
  return timestampToBusinessDate(now.toISOString());
}

/**
 * Every Stage 3 `reason` (AC §18.3): required, trimmed, at most 1000 characters, written to the audit
 * event. The server trims and checks again.
 */
export const REASON_MAX = 1000;

export function reasonSchema(required = true) {
  const base = z
    .string()
    .trim()
    .max(REASON_MAX, `حداکثر ${toPersianDigits(REASON_MAX)} کاراکتر.`);
  return required ? base.min(1, 'دلیل را بنویسید.') : base;
}

/**
 * The first instant of a business date in Asia/Tehran (00:00, +03:30; Iran has no DST since 2022),
 * for a form that picks a date where the contract wants a `Timestamp` (for example a restriction's
 * `endsAt`). Display never converts a `LocalDate` (FTD §3.2); this is only the request value.
 */
export function startOfBusinessDay(date: string): string {
  return new Date(`${date}T00:00:00+03:30`).toISOString();
}

/** Adds calendar days to a `LocalDate` in Asia/Tehran (restriction presets, UI only). */
export function addCalendarDays(date: string, days: number): string {
  const anchor = new Date(`${date}T12:00:00+03:30`);
  anchor.setUTCDate(anchor.getUTCDate() + days);
  return timestampToBusinessDate(anchor.toISOString());
}
